import { useLayoutEffect, useState } from 'preact/hooks';
import type { CheckoutCallbacks, CheckoutText, DonationControls, OnsiteProduct, OpenDonationOptions } from '@/types';
import { CheckoutFlow } from '@/components/CheckoutFlow';
import { logWarn } from '@/sdkConsole';

interface Props {
  domainId: number;
  donations: OnsiteProduct[];
  // Whether the domain opted into expedited checkout, threaded into the
  // donation's checkout flow (single-screen guest checkout for anonymous
  // visitors). Absent is treated as false.
  expeditedCheckoutEnabled?: boolean;
  // Whether this page's checkout offers wallets (see CheckoutFlow).
  walletsEnabled?: boolean;
  // Per-domain chrome overrides, passed through to the donation gate's trust line.
  checkoutText?: CheckoutText;
  // Publishes this launcher's imperative open/close handle to the SDK on mount
  // (and null on unmount), so the always-present ezsubscriptions.openDonation /
  // closeDonation methods can drive the dialog.
  registerControls: (controls: DonationControls | null) => void;
}

// DonationLauncher owns the donation dialog. It renders nothing until opened via
// the SDK controls it publishes: ezsubscriptions.openDonation() / closeDonation()
// and the declarative [data-ezoic-donate] delegation the SDK routes through them.
// There is no fallback button — publishers supply their own trigger.
export function DonationLauncher({ domainId, donations, expeditedCheckoutEnabled, walletsEnabled, checkoutText, registerControls }: Props) {
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);
  // A publisher-requested preset amount (cents) for a variable donation; null
  // shows the picker's default presets. The picker ignores out-of-range values.
  const [requestedAmountCents, setRequestedAmountCents] = useState<number | null>(null);
  // Checkout callbacks for the current open, supplied per openDonation(opts)
  // call (or none). Reset on every open so a callback never leaks to a later
  // donation opened without one.
  const [openCallbacks, setOpenCallbacks] = useState<CheckoutCallbacks>({});

  // dialogProducts is the single requested donation when a productId was given,
  // else every configured donation (the picker handles more than one).
  let dialogProducts = donations;
  if (productId) {
    const match = donations.find((d) => d.externalId === productId);
    if (match) dialogProducts = [match];
  }

  // Single open path so every trigger resets the preset and per-open callbacks
  // consistently — opening without them shows the full picker at its defaults.
  const openDonation = (opts?: OpenDonationOptions) => {
    if (opts?.productId && !donations.some((d) => d.externalId === opts.productId)) {
      logWarn('openDonation: no donation matches productId; showing all donations', opts.productId);
    }
    setProductId(opts?.productId ?? null);
    setRequestedAmountCents(opts?.amountCents ?? null);
    setOpenCallbacks({ onSuccess: opts?.onSuccess, onCancel: opts?.onCancel, onError: opts?.onError });
    setOpen(true);
  };

  // Publish the imperative handle to the SDK synchronously on mount (layout
  // effect, so it is registered before the awaited openDonation() that self-
  // mounts reads it) and clear it on unmount.
  useLayoutEffect(() => {
    registerControls({ open: openDonation, close: () => setOpen(false) });
    return () => registerControls(null);
  }, []);

  if (donations.length === 0 || !open) return null;

  return (
    <CheckoutFlow
      domainId={domainId}
      products={dialogProducts}
      expeditedCheckoutEnabled={expeditedCheckoutEnabled}
      walletsEnabled={walletsEnabled}
      checkoutText={checkoutText}
      initialAmountCents={requestedAmountCents ?? undefined}
      onUnlock={() => setOpen(false)}
      onDismiss={() => setOpen(false)}
      onSuccess={openCallbacks.onSuccess}
      onCancel={openCallbacks.onCancel}
      onError={openCallbacks.onError}
    />
  );
}
